import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, LinearProgress } from '@mui/material';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import HandshakeRoundedIcon from '@mui/icons-material/HandshakeRounded';
import MoneyOffRoundedIcon from '@mui/icons-material/MoneyOffRounded';
import { Link } from 'react-router-dom';

import BiGauge from '../../components/biGauge';
import BiMetric from '../../components/biMetric';
import MonthRangePicker from '../../components/monthRangePicker';
import PageHeader from '../../components/pageHeader';
import { PageIntro } from '../../components/ui';
import { formatMonthParam, getCurrentMonthStart, getMonthsAgoStart } from '../../utils/dateUtils';
import { formatInteger } from '../../utils/numberUtils';
import { formatCurrency } from '../../utils/stringFormat';
import { buildDashboardCobrancaGauges, fetchDashboardCobranca } from './dashboardCobrancaData';
import '../css/biAluguelAccess.css';
import '../css/biInadimplencia.css';
import '../css/biDashboard.css';

const minMonth = new Date(2022, 0, 1);
const getDefaultMonth = () => getMonthsAgoStart(3);

const DashboardCobranca = () => {
    const [dataInicio, setDataInicio] = useState(getDefaultMonth);
    const [dataFim, setDataFim] = useState(getDefaultMonth);
    const [dashboard, setDashboard] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const loadDashboard = useCallback(async (startDate, endDate) => {
        setIsLoading(true);
        const periodDashboard = await fetchDashboardCobranca({
            dataInicio: formatMonthParam(startDate),
            dataFim: formatMonthParam(endDate)
        });
        setDashboard(periodDashboard);
        setIsLoading(false);
    }, []);

    useEffect(() => {
        loadDashboard(dataInicio, dataFim);
    }, [dataFim, dataInicio, loadDashboard]);

    const periodQuery = `?data_inicio=${formatMonthParam(dataInicio)}&data_fim=${formatMonthParam(dataFim)}`;
    const gauges = useMemo(() => (
        dashboard ? buildDashboardCobrancaGauges(dashboard, periodQuery) : []
    ), [dashboard, periodQuery]);

    const handleDateRangeApply = (startDate, endDate) => {
        setDataInicio(startDate);
        setDataFim(endDate);
    };

    return (
        <Box className='mainBox bi-aluguel-access-page bi-dashboard-page'>
            <PageHeader>BI Cobrança</PageHeader>

            <PageIntro eyebrow='BI Cobrança' title='Visão geral da cobrança'>
                Metas de inadimplência, acordos, negativação e baixas no período escolhido.
            </PageIntro>

            <section className='bi-aluguel-access-shell'>
                {isLoading && <LinearProgress className='bi-aluguel-access-loading' />}

                <div className='bi-aluguel-access-filters bi-ge-indications-filters bi-ge-inadimplencia-period-filter bi-dashboard-period-filter'>
                    <MonthRangePicker
                        startDate={dataInicio}
                        endDate={dataFim}
                        minDate={minMonth}
                        maxDate={getCurrentMonthStart()}
                        disabled={isLoading}
                        onApply={handleDateRangeApply}
                    />
                </div>

                {dashboard && (
                    <>
                        <div className='bi-dashboard-gauges'>
                            {gauges.map(({ key, ...gauge }) => <BiGauge key={key} {...gauge} />)}
                        </div>

                        <div className='bi-aluguel-access-metrics bi-dashboard-metrics'>
                            <Link to={`/BICOB/inadimplencia${periodQuery}`} className='bi-dashboard-metric-link'>
                                <BiMetric
                                    icon={MoneyOffRoundedIcon}
                                    label='Atrasados'
                                    value={formatCurrency(dashboard.inadimplencia.total_atrasado)}
                                    subtitle={`${formatInteger(dashboard.inadimplencia.quantidade_atrasado)} faturas`}
                                    tone='blue'
                                />
                            </Link>
                            <Link to='/BICOB/acordos' className='bi-dashboard-metric-link'>
                                <BiMetric
                                    icon={HandshakeRoundedIcon}
                                    label='Acordos'
                                    value={formatCurrency(dashboard.acordos.total)}
                                    subtitle={`${formatInteger(dashboard.acordos.quantidade)} acordos`}
                                    tone='green'
                                />
                            </Link>
                            <Link to='/BICOB/listaSerasa' className='bi-dashboard-metric-link'>
                                <BiMetric
                                    icon={GavelRoundedIcon}
                                    label='Impedimentos Serasa'
                                    value={formatCurrency(dashboard.serasa.total_impedimentos)}
                                    subtitle={`${formatInteger(dashboard.serasa.quantidade_impedimentos)} títulos`}
                                    tone='navy'
                                />
                            </Link>
                        </div>
                    </>
                )}
            </section>
        </Box>
    );
};

export default DashboardCobranca;
